import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert,
    Button,
    Stack,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import { Plus, Trash2, SquarePen, Upload, X } from 'lucide-react';
import { getFeaturedLogos, createFeaturedLogo, updateFeaturedLogo, deleteFeaturedLogo } from '../../Services/ContentHub/FeaturedLogoApi';
import DeleteModal from '../../Components/Modals/DeleteModal';
import PermissionWrapper from '../../Components/Auth/PermissionWrapper';
import { useGlobalSnackbar } from '../../Components/Common/GlobalSanckbar';

const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    });
};

const FeaturedLogos = () => {
    const { showSnackbar } = useGlobalSnackbar();
    const [logos, setLogos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [modalError, setModalError] = useState(null);
    const [fieldErrors, setFieldErrors] = useState({});

    const imageFieldRef = useRef(null);

    // Modal states
    const [openModal, setOpenModal] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [currentLogo, setCurrentLogo] = useState(null);
    const [selectedImage, setSelectedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    // Delete modal
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedLogoId, setSelectedLogoId] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState(null);

    // Fetch logos with useCallback to prevent multiple API calls
    const fetchLogos = useCallback(async (signal) => {
        try {
            setLoading(true);
            const response = await getFeaturedLogos({ signal });
            if (response && response.success) {
                setLogos(response.data);
            } else {
                setError("Failed to load featured logos.");
            }
        } catch (err) {
            if (!signal?.aborted && err.name !== 'CanceledError') {
                setError("Failed to fetch featured logos. Please try again later.");
            }
        } finally {
            if (!signal?.aborted) setLoading(false);
        }
    }, []);

    useEffect(() => {
        const controller = new AbortController();
        fetchLogos(controller.signal);
        return () => controller.abort();
    }, [fetchLogos]);

    const handleOpenModal = (logo = null) => {
        if (logo) {
            setIsEdit(true);
            setCurrentLogo(logo);
            setImagePreview(logo.image);
        } else {
            setIsEdit(false);
            setCurrentLogo(null);
            setImagePreview(null);
        }
        setError(null);
        setModalError(null);
        setFieldErrors({});
        setSelectedImage(null);
        setOpenModal(true);
    };
    const handleCloseModal = () => {
        setError(null);
        setModalError(null);
        setFieldErrors({});
        setOpenModal(false);
        setIsEdit(false);
        setCurrentLogo(null);
        setSelectedImage(null);
        setImagePreview(null);
    };

    const handleImageChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            if (!file.type.startsWith("image/")) {
                setFieldErrors(prev => ({ ...prev, featuredImage: "Only image files (PNG, JPG, WEBP, SVG, GIF) are allowed" }));
                setModalError("File type not supported! Expected an image.");
                showSnackbar("Only image files are allowed", "error");
                e.target.value = "";
                return;
            }

            setSelectedImage(file);
            if (fieldErrors.featuredImage) {
                setFieldErrors(prev => ({ ...prev, featuredImage: null }));
            }
            if (modalError) {
                setModalError(null);
            }

            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemoveImage = () => {
        setSelectedImage(null);
        setImagePreview(null);
        if (fieldErrors.featuredImage) {
            setFieldErrors(prev => ({ ...prev, featuredImage: null }));
        }
    };

    const handleSubmit = async () => {
        setFieldErrors({});
        setModalError(null);

        if (selectedImage && !selectedImage.type.startsWith("image/")) {
            setFieldErrors({ featuredImage: "Only image files are allowed" });
            setModalError("File type not supported! Expected an image.");
            showSnackbar("Only image files are allowed", "error");
            return;
        }

        if (!selectedImage && !isEdit) {
            setFieldErrors({ featuredImage: "Please select an image" });
            setTimeout(() => {
                imageFieldRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
            }, 50);
            return;
        }

        setSubmitting(true);

        const formData = new FormData();
        if (selectedImage) {
            formData.append('featuredImage', selectedImage);
        }

        try {
            if (isEdit) {
                await updateFeaturedLogo(currentLogo.id, formData);
                showSnackbar("Featured logo updated successfully!", "success");
            } else {
                await createFeaturedLogo(formData);
                showSnackbar("Featured logo created successfully!", "success");
            }

            handleCloseModal();
            // Refresh the list
            const controller = new AbortController();
            fetchLogos(controller.signal);
        } catch (err) {
            const errorData = err.response?.data;
            const displayMsg = errorData?.errors?.[0] || errorData?.details?.[0]?.message || errorData?.message || `Failed to ${isEdit ? 'update' : 'create'} featured logo`;

            if (errorData && errorData.details && Array.isArray(errorData.details)) {
                const errorsObj = {};
                errorData.details.forEach(detail => {
                    const fieldName = (detail.field === "image" || detail.field === "file" || detail.field === "featuredImage") ? "featuredImage" : detail.field;
                    errorsObj[fieldName] = detail.message;
                });
                setFieldErrors(errorsObj);

                const firstErrorField = errorData.details[0]?.field;
                setTimeout(() => {
                    if (firstErrorField === "featuredImage" || firstErrorField === "image" || firstErrorField === "file") {
                        imageFieldRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                    }
                }, 50);

                setModalError(displayMsg);
            } else if (errorData && errorData.errors && Array.isArray(errorData.errors) && errorData.errors.length > 0) {
                setFieldErrors({ featuredImage: errorData.errors[0] });
                setModalError(errorData.errors[0]);
            } else {
                setModalError(displayMsg);
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteClick = (id) => {
        setSelectedLogoId(id);
        setDeleteError(null);
        setDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (selectedLogoId) {
            try {
                setIsDeleting(true);
                setDeleteError(null);
                const response = await deleteFeaturedLogo(selectedLogoId);
                if (response && response.success) {
                    setDeleteModalOpen(false);
                    setSelectedLogoId(null);
                    showSnackbar("Featured logo deleted successfully!", "success");

                    // Refresh list
                    const controller = new AbortController();
                    fetchLogos(controller.signal);
                } else {
                    setDeleteError(response?.message || "Failed to delete featured logo");
                }
            } catch (err) {
                console.error("Delete Error:", err);
                const errorMessage = err.response?.data?.message || err.message || "Failed to delete featured logo";
                setDeleteError(errorMessage);
            } finally {
                setIsDeleting(false);
            }
        }
    };

    return (
        <PermissionWrapper permission="view">
            <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#FBFBFB', minHeight: '100vh' }}>
                {/* Header Section */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                    <Typography sx={{ color: '#222', fontSize: { xs: '1.75rem', md: '1.8rem' }, fontWeight: 500 }}>
                        Featured Logos
                    </Typography>
                    <PermissionWrapper permission="create" mode="disable">
                        <Button
                            disableElevation
                            disableRipple
                            disableFocusRipple
                            disableTouchRipple
                            variant="contained"
                            startIcon={<Plus size={18} />}
                            onClick={() => handleOpenModal()}
                            sx={{
                                bgcolor: '#FFBD65',
                                px: 2.5,
                                color: '#fff',
                                borderRadius: '6px',
                                textTransform: 'none',
                                height: '40px',
                                fontSize: '0.85rem'
                            }}
                        >
                            Add Logo
                        </Button>
                    </PermissionWrapper>
                </Box>

                {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

                <Paper sx={{ borderRadius: "8px", border: "1px solid #E8E8E8", overflow: "hidden" }} elevation={0}>
                    <TableContainer>
                        <Table sx={{ minWidth: 950 }}>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#F8FAFC', borderBottom: '2px solid #EEF2F6' }}>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', textAlign: 'center', width: '60px' }}>S.No</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap' }}>Logo</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '130px' }}>Created By</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '130px' }}>Updated By</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '120px' }}>Created At</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '120px' }}>Updated At</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '100px', textAlign: 'right' }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                                            <CircularProgress size={24} sx={{ color: '#FFBD65' }} />
                                        </TableCell>
                                    </TableRow>
                                ) : logos.length > 0 ? (
                                    logos.map((logo, idx) => (
                                        <TableRow key={logo.id} sx={{
                                            transition: 'background-color 0.2s',
                                            '& td': { borderBottom: '1px solid #F0F0F0' },
                                            '&:last-child td': { borderBottom: 0 }
                                        }}>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#666', textAlign: 'center' }}>{idx + 1}</TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5 }}>
                                                <Box component="img"
                                                    src={logo.image} alt={`Logo ${idx + 1}`}
                                                    sx={{ width: 40, height: 40, objectFit: 'contain', bgcolor: '#F9F9F9', borderRadius: '4px', border: '1px solid #F0F0F0', p: 0.5 }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {logo.cuName || '-'}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {logo.uuName || '-'}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {formatDate(logo.createdAt)}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {formatDate(logo.updatedAt)}
                                            </TableCell>
                                            <TableCell align="right" sx={{ py: 1.25, px: 1.5 }}>
                                                <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                    <PermissionWrapper permission="edit" mode="disable">
                                                        <Tooltip title="Edit">
                                                            <IconButton disableFocusRipple disableRipple disableTouchRipple
                                                                size="small"
                                                                sx={{ color: '#FFBD65' }}
                                                                onClick={() => handleOpenModal(logo)}
                                                            >
                                                                <SquarePen size={16} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </PermissionWrapper>
                                                    <PermissionWrapper permission="delete" mode="disable">
                                                        <Tooltip title="Delete">
                                                            <IconButton disableFocusRipple disableRipple disableTouchRipple
                                                                size="small"
                                                                sx={{ color: '#FF4D49' }}
                                                                onClick={() => handleDeleteClick(logo.id)}
                                                            >
                                                                <Trash2 size={16} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </PermissionWrapper>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center" sx={{ py: 4, color: '#AAA', fontSize: '0.8rem' }}>
                                            No featured logos found.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

                {/* Add/Edit Modal */}
                <Dialog open={openModal} onClose={handleCloseModal} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '6px' } }}>
                    <DialogTitle sx={{ fontWeight: 600, color: '#222', fontSize: '1.1rem', pb: 1 }}>
                        {isEdit ? 'Edit Featured Logo' : 'Add Featured Logo'}
                    </DialogTitle>
                    <DialogContent>
                        {modalError && <Alert severity="error" sx={{ mb: 2, fontSize: '0.8rem' }}>{modalError}</Alert>}
                        <Box sx={{ mt: 1 }} ref={imageFieldRef}>
                            <Typography sx={{ mb: 1, fontWeight: 550, color: '#333', fontSize: '0.85rem' }}>Logo Image</Typography>
                            <Box
                                sx={{
                                    border: fieldErrors.featuredImage ? '2px dashed #FF4D49' : '2px dashed #E0E0E0',
                                    borderRadius: '6px',
                                    p: 2,
                                    textAlign: 'center',
                                    bgcolor: '#FAFAFA',
                                    cursor: 'pointer',
                                    position: 'relative',
                                    transition: 'all 0.2s ease-in-out',
                                    '&:hover': {
                                        borderColor: fieldErrors.featuredImage ? '#FF4D49' : '#FFBD65'
                                    }
                                }}
                            >
                                {imagePreview ? (
                                    <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                        <img src={imagePreview} alt="Preview"
                                            style={{
                                                maxHeight: 150,
                                                maxWidth: '100%',
                                                borderRadius: '4px',
                                                border: fieldErrors.featuredImage ? '2px solid #FF4D49' : 'none',
                                                transition: 'all 0.2s ease-in-out'
                                            }}
                                        />
                                        <IconButton
                                            size="small"
                                            onClick={handleRemoveImage}
                                            sx={{
                                                position: 'absolute',
                                                top: -10,
                                                right: -10,
                                                bgcolor: '#FFFFFF',
                                                color: '#EF4444',
                                                boxShadow: '0px 2px 6px rgba(0,0,0,0.2)',
                                                '&:hover': {
                                                    bgcolor: '#FEE2E2',
                                                    color: '#DC2626'
                                                }
                                            }}
                                        >
                                            <X size={14} color="#EF4444" />
                                        </IconButton>
                                    </Box>
                                ) : (
                                    <label style={{ width: '100%', height: '100%', display: 'block', cursor: 'pointer' }}>
                                        <input type="file" accept="image/png, image/jpeg, image/jpg, image/webp, image/gif, image/svg+xml" hidden onChange={handleImageChange} />
                                        <Stack spacing={1} alignItems="center">
                                            <Box sx={{ p: 1.5, borderRadius: '50%', bgcolor: '#FFF8ED', color: fieldErrors.featuredImage ? '#FF4D49' : '#FFBD65', display: 'inline-flex' }}>
                                                <Upload size={20} />
                                            </Box>
                                            <Typography sx={{ fontSize: '0.85rem' }} color={fieldErrors.featuredImage ? 'error' : 'textSecondary'}>Click to upload logo</Typography>
                                            <Typography sx={{ fontSize: '0.75rem' }} color={fieldErrors.featuredImage ? 'error' : 'textSecondary'}>PNG, JPG or SVG (max. 3MB)</Typography>
                                        </Stack>
                                    </label>
                                )}
                            </Box>
                            {fieldErrors.featuredImage && (
                                <Typography sx={{ color: '#FF4D49', fontSize: '0.75rem', mt: 1, fontWeight: 500, transition: 'all 0.2s ease-in-out' }}>
                                    {fieldErrors.featuredImage}
                                </Typography>
                            )}
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ p: 2.5, pt: 1 }}>
                        <Button disableElevation disableFocusRipple disableRipple disableTouchRipple
                            onClick={handleCloseModal}
                            sx={{ borderRadius: '6px', textTransform: 'none', color: '#222', px: 2.5, fontSize: '0.85rem' }}
                        >
                            Cancel
                        </Button>
                        <Button disableElevation disableFocusRipple disableRipple disableTouchRipple
                            onClick={handleSubmit}
                            disabled={submitting}
                            variant="contained"
                            sx={{
                                bgcolor: '#FFBD65',
                                borderRadius: '6px',
                                textTransform: 'none',
                                px: 3,
                                color: '#fff',
                                fontSize: '0.85rem'
                            }}
                        >
                            {submitting ? <CircularProgress size={20} color="inherit" /> : (isEdit ? 'Update' : 'Add')}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Delete Modal */}
                <DeleteModal
                    open={deleteModalOpen}
                    onClose={() => setDeleteModalOpen(false)}
                    onDelete={handleConfirmDelete}
                    title="Delete Featured Logo"
                    description="Are you sure you want to delete this featured logo? This action cannot be undone."
                    error={deleteError}
                    loading={isDeleting}
                />
            </Box>
        </PermissionWrapper>
    );
};

export default FeaturedLogos;
